import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
// Removed FontAwesome icons - using simple text/HTML elements instead

import { useBooking, BOOKING_ACTIONS } from '../../../contexts/BookingContext';
import { venposService2 } from '../../../services/venposService2';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';

// Import step components
import TicketSelection from './steps/TicketSelection';
import DateSelection from './steps/DateSelection';
import UpsellStep from './steps/UpsellStep';
import CustomerDetails from './steps/CustomerDetails';
import PaymentDetails from './steps/PaymentDetails';
import BookingConfirmation from './steps/BookingConfirmation';

// Import images
import navyLogo from '../../../assets/navy_logo_stacked.png';

const BOOKING_JOURNEY_BANNER_CDN =
  'https://BelvoirCastlePull.b-cdn.net/Website/1.webp';

const BookingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 158px;

  @media (max-width: 768px) {
    padding-top: 100px;
  }
`;

const BookingIntroSection = styled.section`
  padding: clamp(1.65rem, 4vw, 2.35rem) clamp(1.25rem, 4vw, 2rem)
    clamp(1rem, 2.5vw, 1.35rem);
  border-top: 1px solid rgba(16, 29, 64, 0.07);
  background: linear-gradient(
    180deg,
    ${props => props.theme.colors.offWhite} 0%,
    rgba(241, 239, 234, 0.35) 100%
  );
`;

const BookingIntroInner = styled.div`
  max-width: 36rem;
  margin: 0 auto;
  text-align: center;
`;

const BookingIntroEyebrow = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.48);
  margin: 0 0 0.5rem;
`;

const BookingIntroSubtitle = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.92rem, 1.85vw, 1.02rem);
  line-height: 1.55;
  color: rgba(16, 29, 64, 0.56);
  margin: 0;
`;

const BookingContent = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  width: 100%;
  padding: clamp(0.85rem, 2.5vw, 1.35rem) clamp(1rem, 4vw, 1.5rem)
    clamp(2rem, 5vw, 3rem);
`;

const ProgressContainer = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: clamp(1rem, 2.5vw, 1.35rem) clamp(1rem, 3vw, 1.35rem);
  margin-bottom: clamp(0.85rem, 2vw, 1.1rem);
  border: 1px solid rgba(16, 29, 64, 0.08);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.92) inset,
    0 14px 42px rgba(16, 29, 64, 0.07);
`;

const ProgressHeader = styled.div`
  text-align: center;
  margin-bottom: clamp(0.85rem, 2vw, 1.15rem);

  h1 {
    font-family: ${props => props.theme.fonts.main};
    font-size: clamp(1.45rem, 3.5vw, 1.85rem);
    font-weight: 700;
    color: ${props => props.theme.colors.primary};
    margin: 0 0 0.35rem;
    line-height: 1.15;
  }

  p {
    font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    margin: 0;
    color: rgba(16, 29, 64, 0.55);
    font-size: clamp(0.88rem, 1.75vw, 0.96rem);
    line-height: 1.45;
  }
`;

const ProgressSteps = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: clamp(0.85rem, 2vw, 1.1rem);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 2px;
    background: rgba(16, 29, 64, 0.08);
    border-radius: 2px;
    z-index: 1;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.65rem;

    &::before {
      display: none;
    }
  }
`;

const ProgressStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
  background: #fff;
  padding: 0 0.65rem;

  @media (max-width: 768px) {
    flex-direction: row;
    width: 100%;
    justify-content: flex-start;
    padding: 0.85rem 1rem;
    border-radius: 12px;
    border: 1px solid rgba(16, 29, 64, 0.07);
    background: ${props =>
      props.$isActive ? 'rgba(241, 239, 234, 0.55)' : '#fff'};
    box-shadow: ${props =>
      props.$isActive
        ? '0 8px 24px rgba(16, 29, 64, 0.07)'
        : '0 1px 2px rgba(16, 29, 64, 0.04)'};
  }
`;

const StepIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.45rem;
  background: ${props => {
    if (props.$isCompleted) return props.theme.colors.accent;
    if (props.$isActive) return props.theme.colors.primary;
    return 'rgba(16, 29, 64, 0.07)';
  }};
  color: ${props => {
    if (props.$isCompleted || props.$isActive) return '#fff';
    return 'rgba(16, 29, 64, 0.42)';
  }};
  font-size: 1.05rem;
  font-weight: 700;
  transition: transform 0.22s ease, box-shadow 0.22s ease;
  box-shadow: ${props =>
    props.$isActive
      ? '0 0 0 3px rgba(157, 109, 73, 0.28)'
      : props.$isCompleted
        ? '0 4px 14px rgba(157, 109, 73, 0.25)'
        : 'none'};

  @media (max-width: 768px) {
    margin-bottom: 0;
    margin-right: 0.95rem;
    width: 38px;
    height: 38px;
  }
`;

const StepLabel = styled.div`
  text-align: center;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.78rem, 1.65vw, 0.88rem);
  color: ${props => {
    if (props.$isCompleted || props.$isActive) return props.theme.colors.primary;
    return 'rgba(16, 29, 64, 0.48)';
  }};
  font-weight: ${props => (props.$isActive ? '600' : '500')};

  @media (max-width: 768px) {
    text-align: left;
  }
`;

const StepContent = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: clamp(1.1rem, 3vw, 1.5rem);
  border: 1px solid rgba(16, 29, 64, 0.08);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.92) inset,
    0 14px 42px rgba(16, 29, 64, 0.07);
  min-height: 400px;
  transition: opacity 0.2s ease;
  contain: layout;
`;

const NavigationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: clamp(0.85rem, 2vw, 1.1rem);
  padding-top: clamp(0.85rem, 2vw, 1.1rem);
  border-top: 1px solid rgba(16, 29, 64, 0.07);
  gap: 0.75rem;

  @media (max-width: 576px) {
    flex-direction: column-reverse;
  }
`;

const NavigationButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 0.72rem 1.65rem;
  border-radius: 999px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.92rem, 2vw, 1rem);
  font-weight: 600;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease,
    transform 0.2s ease;
  border: 2px solid transparent;
  cursor: pointer;
  min-height: 48px;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  @media (max-width: 576px) {
    width: 100%;
  }
`;

const BackButton = styled(NavigationButton)`
  background: transparent;
  color: rgba(16, 29, 64, 0.62);
  border-color: rgba(16, 29, 64, 0.14);

  &:hover:not(:disabled) {
    background: rgba(16, 29, 64, 0.05);
    color: ${props => props.theme.colors.primary};
    border-color: rgba(16, 29, 64, 0.22);
  }
`;

const NextButton = styled(NavigationButton)`
  background: ${props => props.theme.colors.accent};
  color: #fff;
  border-color: ${props => props.theme.colors.accent};
  min-width: 158px;
  box-shadow: 0 6px 20px rgba(157, 109, 73, 0.28);

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.primary};
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 10px 28px rgba(16, 29, 64, 0.18);
  }

  @media (max-width: 576px) {
    min-width: 100%;
  }
`;

const ErrorMessage = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: rgba(220, 53, 69, 0.08);
  color: #a71d2a;
  padding: 0.95rem 1.1rem;
  border-radius: 12px;
  margin-bottom: 1rem;
  text-align: center;
  border: 1px solid rgba(220, 53, 69, 0.22);
  font-size: 0.92rem;
  line-height: 1.45;
`;

const LoadingScreen = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 3rem 1rem;
  background: ${props => props.theme.colors.offWhite};
`;

const LoadingLogoContainer = styled.div`
  margin-bottom: 2rem;
  animation: fadeInScale 0.8s ease-out;
  
  @keyframes fadeInScale {
    from {
      opacity: 0;
      transform: scale(0.8);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const LoadingLogo = styled.img`
  width: 180px;
  height: auto;
  animation: pulse 2s ease-in-out infinite;
  
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.85;
      transform: scale(1.05);
    }
  }
`;

const LoadingText = styled.div`
  font-family: ${props => props.theme.fonts.main};
  color: ${props => props.theme.colors.primary};
  font-size: clamp(1.15rem, 2.5vw, 1.35rem);
  font-weight: 700;
  margin-bottom: 0.5rem;
  animation: fadeIn 1s ease-out 0.3s both;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const LoadingStrapline = styled.div`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: rgba(16, 29, 64, 0.52);
  font-size: clamp(0.9rem, 1.9vw, 1rem);
  animation: fadeIn 1s ease-out 0.6s both;
`;

const LoadingSpinnerDots = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1.5rem;
  animation: fadeIn 1s ease-out 0.9s both;
  
  span {
    width: 10px;
    height: 10px;
    background: ${props => props.theme.colors.accent};
    border-radius: 50%;
    animation: bounce 1.4s ease-in-out infinite;
    
    &:nth-child(1) { animation-delay: 0s; }
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
  
  @keyframes bounce {
    0%, 80%, 100% {
      transform: translateY(0);
      opacity: 0.7;
    }
    40% {
      transform: translateY(-15px);
      opacity: 1;
    }
  }
`;

const BookingJourney2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    currentStep,
    totalSteps,
    locationId,
    pageId,
    selectedTickets,
    selectedDate,
    selectedTimeSlot,
    availableUpsells,
    selectedUpsells,
    customerDetails,
    paymentDetails,
    loading,
    error,
    dispatch,
    canProceedToNextStep,
    calculateTotal,
    getFamilySaverCode
  } = useBooking();

  const [tickets, setTickets] = useState([]);
  const [availableDates, setAvailableDates] = useState({});
  const [timeSlots, setTimeSlots] = useState([]);
  const [basketId, setBasketId] = useState(null);
  const [bookingReference, setBookingReference] = useState(null);
  const [venposBasket, setVenposBasket] = useState(null);
  const [appliedSaverCode, setAppliedSaverCode] = useState(null);
  const [basketCustomerId, setBasketCustomerId] = useState(null);
  const [basketOrderId, setBasketOrderId] = useState(null);
  const [preflightDone, setPreflightDone] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [upsellsLoading, setUpsellsLoading] = useState(false);

  // Initialize booking with location and page ID from URL params or state
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const locationIdParam = urlParams.get('locationId') || location.state?.locationId;
    const pageIdParam = urlParams.get('pageId') || location.state?.pageId;
    
    // Remove date parameter from URL if it exists - users must manually select a date
    if (urlParams.has('date')) {
      urlParams.delete('date');
      window.history.replaceState(null, '', `${location.pathname}?${urlParams.toString()}`);
    }

    if (locationIdParam) {
      dispatch({ type: BOOKING_ACTIONS.SET_LOCATION_ID, payload: locationIdParam });
    }
    if (pageIdParam) {
      dispatch({ type: BOOKING_ACTIONS.SET_PAGE_ID, payload: pageIdParam });
    }
  }, [location, dispatch]);

  // Load tickets when component mounts
  useEffect(() => {
    const loadTickets = async () => {
      if (!pageId) return;

      try {
        dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: true });

        const response = await venposService2.getWebSaleItems(
          locationId,
          pageId,
          selectedDate?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0]
        );

        // Normalize various possible response shapes
        const items = (response?.data?.webSaleItems)
          || (response?.webSaleItems)
          || (response?.data?.items)
          || (response?.items)
          || [];

        // Ensure required fields exist; provide safe defaults
        // Preserve any quantities already chosen
        const existingQtyById = new Map((selectedTickets || []).map(t => [String(t.webSaleItemId), Number(t.quantity || 0)]));
        const normalized = items.map((it) => {
          const id = it.webSaleItemId || it.id || it.itemId || it.WebSaleItemId || it.WebSaleItemID;
          return {
            webSaleItemId: id,
            webSaleItemTypeID: it.webSaleItemTypeID || it.itemTypeId || it.WebSaleItemTypeID,
            name: it.name || it.title || 'Ticket',
            description: it.description || it.DESC || '',
            price: typeof it.price === 'number' ? it.price : (parseFloat(it.price) || parseFloat(it.unitPrice) || 0),
            maxPurchaseQty: it.maxPurchaseQty || it.maxQty || 10,
            quantity: existingQtyById.get(String(id)) || 0
          };
        }).filter(it => it.webSaleItemId);

        setTickets(normalized);
        // Initialize selected tickets in context so quantity updates work
        dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TICKETS, payload: normalized });
      } catch (err) {
        dispatch({ type: BOOKING_ACTIONS.SET_ERROR, payload: 'Failed to load tickets. Please try again.' });

      } finally {
        dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
      }
    };

    loadTickets();
  }, [pageId, locationId, selectedDate, dispatch]);

  // Load time slots (including dates) when tickets are selected - following API documentation flow
  useEffect(() => {
    const loadTimeSlotsAfterTickets = async () => {
      if (!pageId || selectedTickets.length === 0) return;

      try {
        const selectedItems = selectedTickets
          .filter(ticket => ticket.quantity > 0)
          .map(ticket => ({
            pageType: 'WebPage',
            pageId: pageId,
            webSaleItemId: ticket.webSaleItemId,
            quantity: ticket.quantity,
            webSaleItemTypeID: ticket.webSaleItemTypeID,
            name: ticket.name
          }));

        const response = await venposService2.getTimeSlots(
          locationId,
          pageId,
          new Date().toISOString().split('T')[0], // Use current date as starting point
          selectedItems
        );

        const slots = response?.data?.timeSlots || [];

        setTimeSlots(slots);

        // Extract available dates from time slots for the DateSelection component
        if (slots.length > 0) {
          const datesMap = {};
          slots.forEach(slot => {
            if (slot.date) {
              const dateKey = slot.date;
              datesMap[dateKey] = (datesMap[dateKey] || 0) + 1;
            }
          });
          setAvailableDates(datesMap);
        }
      } catch (err) {

      }
    };

    loadTimeSlotsAfterTickets();
  }, [pageId, locationId, selectedTickets, dispatch]);

  // Load time slots when date is selected (for getting time slots for specific date)
  useEffect(() => {
    const loadTimeSlotsForDate = async () => {
      if (!pageId || !selectedDate || selectedTickets.length === 0) return;

      try {
        const selectedItems = selectedTickets
          .filter(ticket => ticket.quantity > 0)
          .map(ticket => ({
            pageType: 'WebPage',
            pageId: pageId,
            webSaleItemId: ticket.webSaleItemId,
            quantity: ticket.quantity,
            webSaleItemTypeID: ticket.webSaleItemTypeID,
            name: ticket.name
          }));

        const response = await venposService2.getTimeSlots(
          locationId,
          pageId,
          selectedDate.toISOString().split('T')[0],
          selectedItems
        );

        const slots = response?.data?.timeSlots || [];

        setTimeSlots(slots);
      } catch (err) {

      }
    };

    loadTimeSlotsForDate();
  }, [pageId, locationId, selectedDate, selectedTickets, dispatch]);

  // Enhanced basket management following API documentation
  const createAndValidateBasket = async (forceNew = false) => {
    try {

      // If we already have a valid basket and not forcing new, just refresh it
      if (basketId && !forceNew) {

        return await refreshExistingBasket();
      }

      // Prepare basket items according to API specification with event metadata
      const basketItems = selectedTickets
        .filter(ticket => (ticket.quantity || 0) > 0)
        .map(ticket => ({
          pageType: 'WebPage',
          pageId: pageId,
          webSaleItemId: ticket.webSaleItemId,
          webSaleItemTypeID: ticket.webSaleItemTypeID,
          quantity: ticket.quantity,
          selectedDate: selectedTimeSlot?.time ?
            `${(selectedDate || new Date()).toISOString().split('T')[0]}T${selectedTimeSlot.time}:00` :
            (selectedDate || new Date()).toISOString().split('T')[0],
          reservationExpiryDateTime: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
          name: ticket.name,
          giftAidEligible: false,
          isSysGift: false,
          isSysMemberCard: false,
          // Include event metadata from selected time slot for proper validation
          eventId: selectedTimeSlot?.eventId || null,
          eventCapacityUsageId: selectedTimeSlot?.eventCapacityUsageId || null
        }));

      if (basketItems.length === 0) {
        throw new Error('No valid basket items');
      }

      // Create basket with validation
      const basketResponse = await venposService2.createBasketWithValidation(
        locationId,
        basketItems,
        customerDetails
      );

      if (basketResponse.isError) {
        throw new Error(basketResponse.error || 'Failed to create basket');
      }

      const newBasketId = basketResponse.data.basketId;
      const newOrderId = basketResponse.data.orderId;
      const newCustomerId = basketResponse.data.customerId;

      setBasketId(newBasketId);
      setBasketOrderId(newOrderId);
      setBasketCustomerId(newCustomerId);

      // Apply family saver discount if applicable
      const saverCode = getFamilySaverCode();
      if (saverCode) {

        setAppliedSaverCode(saverCode);

        try {
          const voucherResponse = await venposService2.applyVoucherCode(
            locationId,
            newBasketId,
            saverCode
          );

          if (voucherResponse.isError) {

          } else {

          }
        } catch (error) {

        }
      }

      // Load final basket with all discounts applied to get totals
      const loadPayload = {
        pageId,
        items: basketItems,
        giftAidSelected: false,
        discounts: saverCode ? [saverCode] : [],
        discountsFromMembership: [],
        // Include the newly created basket's IDs for consistency
        customerId: newCustomerId,
        orderId: newOrderId
      };

      // Include customer details if available for consistency
      if (customerDetails?.firstName && customerDetails?.lastName && customerDetails?.email) {
        loadPayload.customer = {
          firstName: customerDetails.firstName,
          lastName: customerDetails.lastName,
          email: customerDetails.email,
          mobile: customerDetails.phone || customerDetails.mobile || '',
          addressFirstLine: customerDetails.address?.line1 || '',
          addressSecondLine: customerDetails.address?.line2 || '',
          cityTown: customerDetails.address?.city || '',
          postCode: customerDetails.address?.postcode || '',
          selectedCountry: customerDetails.address?.country || 'UK'
        };
      }

      const correctFamilySaverCode = getFamilySaverCode(); // Get correct code based on PAID tickets only
      const finalBasket = await venposService2.loadBasket(locationId, loadPayload, correctFamilySaverCode);
      setVenposBasket(finalBasket);

      // Update stored basket IDs from the response
      if (finalBasket?.data?.customerId && !basketCustomerId) {
        setBasketCustomerId(finalBasket.data.customerId);
      }
      if (finalBasket?.data?.orderId && !basketOrderId) {
        setBasketOrderId(finalBasket.data.orderId);
      }

      return {
        basketId: newBasketId,
        basketDetails: finalBasket,
        success: true
      };

    } catch (error) {

      throw error;
    }
  };

  // Helper function to refresh existing basket without creating new one
  const refreshExistingBasket = async () => {
    try {

      const basketItems = selectedTickets
        .filter(ticket => (ticket.quantity || 0) > 0)
        .map(ticket => ({
          pageType: 'WebPage',
          pageId: pageId,
          webSaleItemId: ticket.webSaleItemId,
          webSaleItemTypeID: ticket.webSaleItemTypeID,
          quantity: ticket.quantity,
          selectedDate: selectedTimeSlot?.time ?
            `${(selectedDate || new Date()).toISOString().split('T')[0]}T${selectedTimeSlot.time}:00` :
            (selectedDate || new Date()).toISOString().split('T')[0],
          reservationExpiryDateTime: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
          name: ticket.name,
          giftAidEligible: false,
          isSysGift: false,
          isSysMemberCard: false,
          eventId: selectedTimeSlot?.eventId || null,
          eventCapacityUsageId: selectedTimeSlot?.eventCapacityUsageId || null
        }));

      if (basketItems.length === 0) {
        throw new Error('No valid basket items');
      }

      // Apply family saver discount if applicable
      const saverCode = getFamilySaverCode();
      if (saverCode) {

        setAppliedSaverCode(saverCode);

        try {
          const voucherResponse = await venposService2.applyVoucherCode(
            locationId,
            basketId,
            saverCode
          );

          if (voucherResponse.isError) {

          } else {

          }
        } catch (error) {

        }
      }

      // Load final basket with all discounts applied to get totals
      // IMPORTANT: Include existing basket's customer and order IDs to prevent creating new basket
      const loadPayload = {
        pageId,
        items: basketItems,
        giftAidSelected: false,
        discounts: saverCode ? [saverCode] : [],
        discountsFromMembership: [],
        // Use stored basket's IDs to prevent creating new basket
        customerId: basketCustomerId || customerDetails?.webCustomerId || null,
        orderId: basketOrderId || basketId // Use stored orderId or fallback to basketId
      };

      // Include customer details if available for consistency
      if (customerDetails?.firstName && customerDetails?.lastName && customerDetails?.email) {
        loadPayload.customer = {
          firstName: customerDetails.firstName,
          lastName: customerDetails.lastName,
          email: customerDetails.email,
          mobile: customerDetails.phone || customerDetails.mobile || '',
          addressFirstLine: customerDetails.address?.line1 || '',
          addressSecondLine: customerDetails.address?.line2 || '',
          cityTown: customerDetails.address?.city || '',
          postCode: customerDetails.address?.postcode || '',
          selectedCountry: customerDetails.address?.country || 'UK'
        };
      }

      const correctFamilySaverCode = getFamilySaverCode();
      const refreshedBasket = await venposService2.loadBasket(locationId, loadPayload, correctFamilySaverCode);
      setVenposBasket(refreshedBasket);

      // Update stored basket IDs from the response if they're missing
      if (refreshedBasket?.data?.customerId && !basketCustomerId) {
        setBasketCustomerId(refreshedBasket.data.customerId);
      }
      if (refreshedBasket?.data?.orderId && !basketOrderId) {
        setBasketOrderId(refreshedBasket.data.orderId);
      }

      return {
        basketId,
        basketDetails: refreshedBasket,
        success: true
      };

    } catch (error) {

      throw error;
    }
  };

  // Preflight util for step 4 preparation
  const runPreflight = async () => {
    try {

      // If we don't have a basket yet, create one
      if (!basketId) {

        await createAndValidateBasket();
        return;
      }

      // Otherwise, just refresh the existing basket to get updated totals

      await refreshExistingBasket();

    } catch (e) {

    } finally {
      setPreflightDone(true);
    }
  };

  // Create basket early after time slot selection (for upsell timing)
  useEffect(() => {
    const createBasketEarly = async () => {
      // Only create basket early if we need it for upsells and don't already have one
      if (selectedTimeSlot && selectedTickets?.length > 0 && !basketId && currentStep === 3) {

        try {
          await createAndValidateBasket();
        } catch (error) {

        }
      }
    };

    createBasketEarly();
  }, [selectedTimeSlot, selectedTickets, basketId, currentStep]);

  // Trigger preflight on entering step 5 or when we have all required data
  useEffect(() => {
    if (currentStep === 5 && !preflightDone && selectedTickets?.length > 0 && selectedDate && selectedTimeSlot) {
      runPreflight();
    }
  }, [currentStep, preflightDone, selectedTickets, selectedDate, selectedTimeSlot]);

  // Load upsells when moving to step 3 (after date/time selection)
  useEffect(() => {
    const loadUpsells = async () => {
      if (currentStep === 3 && selectedTickets?.length > 0 && selectedDate && selectedTimeSlot && availableUpsells.length === 0) {
        setUpsellsLoading(true);
        try {

          // Prepare items for upsell request - match exact structure from working example
          const items = selectedTickets
            .filter(ticket => ticket.quantity > 0)
            .map(ticket => ({
              pageType: 'WebPage',
              pageId: pageId,
              webSaleItemId: ticket.webSaleItemId,
              quantity: ticket.quantity,
              webSaleItemTypeID: ticket.webSaleItemTypeID,
              name: ticket.name,
              selectedDate: selectedDate.toISOString().split('T')[0] + 'T' + (selectedTimeSlot.time || '10:00') + ':00',
              reservationExpiryDateTime: new Date(Date.now() + 30 * 60 * 1000).toISOString(), // 30 minutes from now
              eventCapacityUsageId: selectedTimeSlot.eventCapacityUsageId || null,
              eventId: selectedTimeSlot.eventId || null,
              fixedDonationAmount: 0,
              giftAidEligible: false,
              isSysGift: false,
              isSysMemberCard: false
            }));

          const response = await venposService2.getUpsells(
            locationId,
            pageId,
            'Event',
            'OnScreenCalendar',
            items
          );

          if (response?.data?.upsells) {
            dispatch({ type: BOOKING_ACTIONS.SET_AVAILABLE_UPSELLS, payload: response.data.upsells });
          }
        } catch (error) {

          // Don't block the flow if upsells fail to load
          dispatch({ type: BOOKING_ACTIONS.SET_AVAILABLE_UPSELLS, payload: [] });
        } finally {
          setUpsellsLoading(false);
        }
      }
    };

    loadUpsells();
  }, [currentStep, selectedTickets, selectedDate, selectedTimeSlot, pageId, locationId, availableUpsells.length, dispatch]);

  const handleNext = async () => {
    if (!canProceedToNextStep()) {
      return;
    }

    try {
      // Prevent rapid state changes during transition
      dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: true });

      // Handle step-specific logic
      if (currentStep === 5) {
        // Final step - process payment and confirm booking
        await processBooking();
      } else {
        // If moving from 4 -> 5, run preflight before navigating so totals are ready
        if (currentStep === 4 && selectedTickets?.length > 0 && selectedDate && selectedTimeSlot && !preflightDone) {
          try {
            await runPreflight();
            // Add a small delay to prevent rapid state changes
            await new Promise(resolve => setTimeout(resolve, 100));
          } catch (error) {

            // Continue anyway - the payment step can handle fallbacks
          }
        }

        // Use requestAnimationFrame to smooth the transition
        requestAnimationFrame(() => {
          dispatch({ type: BOOKING_ACTIONS.NEXT_STEP });
          dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
        });
      }
    } catch (error) {

      dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
      dispatch({
        type: BOOKING_ACTIONS.SET_ERROR,
        payload: 'Navigation failed. Please try again.'
      });
    }
  };

  const handleBack = () => {
    dispatch({ type: BOOKING_ACTIONS.PREV_STEP });
  };

  const processBooking = async () => {
    try {
      dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: true });

      let finalBasketId = basketId;

      // Create basket if not already created (fallback)
      if (!finalBasketId) {

        const basketResult = await createAndValidateBasket(true); // Force new basket creation
        finalBasketId = basketResult.basketId;
      } else {
        // Refresh existing basket to ensure we have latest totals

        await refreshExistingBasket();
      }

      if (!finalBasketId) {
        throw new Error('Failed to create or retrieve basket');
      }

      // Calculate final amount from venpos basket (use finalPrice, not totalAmount)
      const finalAmount = venposBasket?.data?.finalPrice ??
                         venposBasket?.finalPrice ??
                         calculateTotal();

      // Handle payment completion (skip for free bookings)
      if (finalAmount > 0) {
        // Payment should already be completed by Payment Sense integration
        if (!paymentResult) {
          throw new Error('Payment not completed. Please complete payment first.');
        }

        // Complete payment with Venpos using Payment Sense result
        const paymentComplete = await venposService2.completePayment(
          locationId,
          finalBasketId,
          paymentResult.sessionId || 'payment-sense-token',
          paymentResult
        );

        if (paymentComplete.isError) {
          throw new Error(paymentComplete.error || 'Payment completion with Venpos failed');
        }

      } else {

      }

      // Confirm booking
      // Collect all applied discount codes from current basket (e.g., ADMIN100)


      const basketDiscountCodes = (venposBasket?.data?.discounts || [])
        .map(d => d?.code)
        .filter(Boolean);

      const bookingResponse = await venposService2.confirmBooking(
        finalBasketId,
        locationId,
        {
          selectedDate: selectedDate.toISOString().split('T')[0],
          selectedTimeSlot: selectedTimeSlot,
          selectedTickets: selectedTickets,
          pageId: pageId,
          totalAmount: finalAmount,
          paymentToken: paymentResult?.sessionId || paymentResult?.transactionId || 'FREE_BOOKING',
          transactionId: paymentResult?.transactionId || 'FREE_BOOKING',
          customerDetails: customerDetails,
          paymentDetails: paymentResult || { method: 'free_booking' },
          // Merge basket discounts (e.g., ADMIN100) with any saver codes
          discounts: [
            ...basketDiscountCodes,
            ...(appliedSaverCode ? [appliedSaverCode] : []),
            ...(getFamilySaverCode() ? [getFamilySaverCode()] : [])
          ],
          familySaverCode: getFamilySaverCode()
        }
      );

      const newBookingReference = bookingResponse.data?.bookingReference ||
                                  bookingResponse.bookingReference ||
                                  `BC${Date.now()}`;

      setBookingReference(newBookingReference);

      dispatch({
        type: BOOKING_ACTIONS.SET_BOOKING_REFERENCE,
        payload: newBookingReference
      });

      dispatch({
        type: BOOKING_ACTIONS.SET_BOOKING_SUMMARY,
        payload: {
          totalAmount: finalAmount,
          bookingReference: newBookingReference,
          confirmationEmail: customerDetails.email
        }
      });

      dispatch({ type: BOOKING_ACTIONS.NEXT_STEP });

    } catch (err) {

      dispatch({
        type: BOOKING_ACTIONS.SET_ERROR,
        payload: err.message || 'Booking failed. Please try again.'
      });
    } finally {
      dispatch({ type: BOOKING_ACTIONS.SET_LOADING, payload: false });
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <TicketSelection
            tickets={tickets}
            selectedTickets={selectedTickets}
            onTicketUpdate={(ticketId, quantity) => {
              dispatch({
                type: BOOKING_ACTIONS.UPDATE_TICKET_QUANTITY,
                payload: { ticketId, quantity }
              });
            }}
            onContinue={handleNext}
          />
        );
      case 2:
        return (
          <DateSelection
            availableDates={availableDates}
            selectedDate={selectedDate}
            timeSlots={timeSlots}
            selectedTimeSlot={selectedTimeSlot}
            onDateSelect={(date) => {
              dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_DATE, payload: date });
              // Date no longer synced to URL - users must select manually each time
            }}
            onTimeSlotSelect={async (timeSlot) => {
              // Proactively resolve capacity usages following the real Venpos flow
              try {
                const items = selectedTickets
                  .filter(ticket => (ticket.quantity || 0) > 0)
                  .map(ticket => ({
                    pageType: 'WebPage',
                    pageId: pageId,
                    webSaleItemId: ticket.webSaleItemId,
                    webSaleItemTypeID: ticket.webSaleItemTypeID,
                    quantity: ticket.quantity,
                    selectedDate: selectedDate.toISOString().split('T')[0] + 'T' + (timeSlot.time || timeSlot) + ':00',
                    fixedDonationAmount: 0,
                    name: ticket.name,
                    giftAidEligible: false,
                    isSysGift: false,
                    isSysMemberCard: false,
                    reservationExpiryDateTime: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
                    // Include eventId from selected time slot if available
                    ...(timeSlot.eventId && { eventId: timeSlot.eventId })
                  }));

                // Step 1: Add event capacity usages (this reserves the capacity and returns eventCapacityUsageId)

                const capacityResponse = await venposService2.addEventCapacityUsages(
                  locationId,
                  pageId,
                  items,
                  null,
                  []
                );

                if (!capacityResponse.isError && capacityResponse.data) {

                  // Step 2: Extract eventCapacityUsageId from response and update the timeSlot
                  // The response should contain the items with eventCapacityUsageId populated
                  const responseItems = capacityResponse.data.items || capacityResponse.data || [];
                  if (responseItems.length > 0 && responseItems[0].eventCapacityUsageId) {
                    const enrichedTimeSlot = {
                      ...timeSlot,
                      eventCapacityUsageId: responseItems[0].eventCapacityUsageId,
                      eventId: responseItems[0].eventId || timeSlot.eventId
                    };

                    dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TIME_SLOT, payload: enrichedTimeSlot });

                    // Step 3: Validate times (as per real flow)
                    const itemsWithCapacityId = items.map(item => ({
                      ...item,
                      eventCapacityUsageId: responseItems[0].eventCapacityUsageId,
                      eventId: responseItems[0].eventId || timeSlot.eventId
                    }));

                    await venposService2.validateBasketTimes(locationId, itemsWithCapacityId);
                  }
                } else {

                  // Fallback: if capacity reservation failed, at least store the raw timeSlot
                  dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TIME_SLOT, payload: timeSlot });
                }
              } catch (e) {

                // Fallback on error as well
                dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TIME_SLOT, payload: timeSlot });
              }
            }}
          />
        );
      case 3:
        return (
          <UpsellStep
            upsells={availableUpsells}
            locationId={locationId}
            pageId={pageId}
            selectedUpsells={selectedUpsells}
            onUpsellSelection={(upsell, quantity) => {
              if (quantity === 0) {
                // Remove upsell from selection
                dispatch({
                  type: BOOKING_ACTIONS.SET_SELECTED_UPSELLS,
                  payload: selectedUpsells.filter(u => u.id !== upsell.id)
                });
              } else {
                // Add or update upsell in selection
                const existingIndex = selectedUpsells.findIndex(u => u.id === upsell.id);
                if (existingIndex >= 0) {
                  const updated = [...selectedUpsells];
                  updated[existingIndex] = { ...upsell, quantity };
                  dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_UPSELLS, payload: updated });
                } else {
                  dispatch({
                    type: BOOKING_ACTIONS.SET_SELECTED_UPSELLS,
                    payload: [...selectedUpsells, { ...upsell, quantity }]
                  });
                }
              }
            }}
            onUpsellItemAdd={async (upsellItems) => {

              try {
                // Add upsell items to the main ticket selection for local state
                const newTickets = [...selectedTickets];

                upsellItems.forEach(upsellItem => {
                  const existingIndex = newTickets.findIndex(t => t.webSaleItemId === upsellItem.webSaleItemId);
                  if (existingIndex >= 0) {
                    // Update existing ticket quantity
                    newTickets[existingIndex].quantity += upsellItem.quantity;
                  } else {
                    // Add new ticket
                    newTickets.push({
                      webSaleItemId: upsellItem.webSaleItemId,
                      webSaleItemTypeID: upsellItem.webSaleItemTypeID,
                      name: upsellItem.name,
                      description: upsellItem.description,
                      price: upsellItem.price,
                      maxPurchaseQty: upsellItem.maxPurchaseQty,
                      quantity: upsellItem.quantity,
                      // Add upsell context
                      isUpsellItem: true,
                      parentUpsellId: upsellItem.parentUpsellId,
                      parentUpsellName: upsellItem.parentUpsellName,
                      // Add event time, questions, and modifiers
                      selectedEventTime: upsellItem.selectedEventTime,
                      questionAnswers: upsellItem.questionAnswers,
                      modifierSelection: upsellItem.modifierSelection
                    });
                  }
                });

                dispatch({ type: BOOKING_ACTIONS.SET_SELECTED_TICKETS, payload: newTickets });

                // Update selected upsells to show they have items selected
                const updatedSelectedUpsells = selectedUpsells.map(upsell => {
                  const hasItems = upsellItems.some(item => item.parentUpsellId === upsell.id);
                  return hasItems ? { ...upsell, quantity: (upsell.quantity || 0) + 1 } : upsell;
                });

                // Add new upsells if they don't exist
                const newUpsells = upsellItems
                  .filter(item => !selectedUpsells.some(u => u.id === item.parentUpsellId))
                  .map(item => ({
                    id: item.parentUpsellId,
                    name: item.parentUpsellName,
                    quantity: 1
                  }));

                dispatch({
                  type: BOOKING_ACTIONS.SET_SELECTED_UPSELLS,
                  payload: [...updatedSelectedUpsells, ...newUpsells]
                });

                // If we have a basket, add items to the VenPos basket
                if (basketId) {

                  // Prepare items for basket addition
                  const basketItems = [];

                  upsellItems.forEach(item => {
                    // Add the main item
                    basketItems.push({
                      pageType: 'WebPage',
                      pageId: pageId,
                      webSaleItemId: item.webSaleItemId,
                      quantity: item.quantity,
                      webSaleItemTypeID: item.webSaleItemTypeID,
                      name: item.name,
                      selectedDate: item.selectedDate || selectedDate?.toISOString().split('T')[0] + 'T' + (selectedTimeSlot?.time || '10:00') + ':00',
                      reservationExpiryDateTime: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
                      eventCapacityUsageId: item.selectedEventTime?.eventId || selectedTimeSlot?.eventCapacityUsageId || null,
                      eventId: item.selectedEventTime?.eventId || selectedTimeSlot?.eventId || null,
                      fixedDonationAmount: 0,
                      giftAidEligible: false,
                      isSysGift: false,
                      isSysMemberCard: false,
                      // Add upsell context
                      parentUpsellId: item.parentUpsellId,
                      parentUpsellName: item.parentUpsellName,
                      // Store questions and modifiers for basket load
                      questionAnswers: item.questionAnswers || {},
                      modifierSelection: item.modifierSelection || null
                    });
                  });

                  // Add items to basket using VenPos API
                  await venposService2.addToBasket(basketId, locationId, basketItems);

                } else {

                }

              } catch (error) {

                // Don't block the UI flow if basket addition fails - silently continue
              }
            }}
            selectedDate={selectedDate}
            loading={upsellsLoading}
          />
        );
      case 4:
        return (
          <CustomerDetails
            customerDetails={customerDetails}
            onUpdate={(details) => {
              dispatch({ type: BOOKING_ACTIONS.UPDATE_CUSTOMER_DETAILS, payload: details });
            }}
            onAddressUpdate={(address) => {
              dispatch({ type: BOOKING_ACTIONS.UPDATE_CUSTOMER_ADDRESS, payload: address });
            }}
          />
        );
      case 5:
        return (
          <PaymentDetails
            paymentDetails={paymentDetails}
            totalAmount={calculateTotal()}
            customerDetails={customerDetails}
            basketId={basketId}
            locationId={locationId}
            venposBasket={venposBasket}
            appliedSaverCode={appliedSaverCode}
            onUpdate={(details) => {
              dispatch({ type: BOOKING_ACTIONS.UPDATE_PAYMENT_DETAILS, payload: details });
            }}
            onCardUpdate={(cardDetails) => {
              dispatch({ type: BOOKING_ACTIONS.UPDATE_CARD_DETAILS, payload: cardDetails });
            }}
            onBasketUpdate={(updatedBasket) => {

              // Ensure the basket data is properly wrapped for consistency
              const wrappedBasket = updatedBasket?.data ? updatedBasket : { data: updatedBasket };
              setVenposBasket(wrappedBasket);

            }}
            onPaymentSuccess={async (paymentResult) => {

              // Store payment result and complete booking process
              setPaymentResult(paymentResult);

              try {
                // Complete the booking in Venpos system
                await processBooking();
              } catch (error) {

                // Handle booking completion failure
                dispatch({
                  type: BOOKING_ACTIONS.SET_ERROR,
                  payload: 'Payment successful but booking completion failed. Please contact support.'
                });
              }
            }}
            onPaymentFailure={(error) => {

              // Handle payment failure (stay on payment page)
            }}
          />
        );
      case 6:
        return (
          <BookingConfirmation
            bookingReference={bookingReference}
            selectedTickets={selectedTickets}
            selectedDate={selectedDate}
            selectedTimeSlot={selectedTimeSlot}
            customerDetails={customerDetails}
            totalAmount={calculateTotal()}
          />
        );
      default:
        return null;
    }
  };

  const getStepTitle = () => {
    const titles = {
      1: 'Select Your Tickets',
      2: 'Choose Date & Time',
      3: 'Enhance Your Visit',
      4: 'Your Details',
      5: 'Payment',
      6: 'Booking Confirmation'
    };
    return titles[currentStep] || '';
  };

  const getStepDescription = () => {
    const descriptions = {
      1: 'Choose the tickets you\'d like to purchase',
      2: 'Select your preferred visit date and time',
      3: 'Add special experiences to make your visit memorable',
      4: 'Enter your contact information',
      5: 'Complete your payment',
      6: 'Your booking has been confirmed'
    };
    return descriptions[currentStep] || '';
  };

  if (loading && currentStep === 1) {
    return (
      <BookingContainer>
        <LoadingScreen>
          <LoadingLogoContainer>
            <LoadingLogo src={navyLogo} alt="Belvoir Castle" />
          </LoadingLogoContainer>
          <LoadingText>Loading Your Experience</LoadingText>
          <LoadingStrapline>Building tomorrow's experiences today</LoadingStrapline>
          <LoadingSpinnerDots>
            <span></span>
            <span></span>
            <span></span>
          </LoadingSpinnerDots>
        </LoadingScreen>
      </BookingContainer>
    );
  }

  return (
    <BookingContainer>
      <ParallaxBanner
        welcomeText="Book online"
        title="Book your visit"
        subtitle="YOUR CASTLE, YOUR CROWD, YOUR SAVING"
        backgroundImage={BOOKING_JOURNEY_BANNER_CDN}
      />

      <BookingIntroSection>
        <BookingIntroInner>
          <BookingIntroEyebrow>Secure checkout</BookingIntroEyebrow>
          <BookingIntroSubtitle>
            Complete your booking in a few steps — your selection and payment are processed securely.
          </BookingIntroSubtitle>
        </BookingIntroInner>
      </BookingIntroSection>

      <BookingContent>
        <ProgressContainer>
          <ProgressHeader>
            <h1>{getStepTitle()}</h1>
            <p>{getStepDescription()}</p>
          </ProgressHeader>

          <ProgressSteps>
            {[1, 2, 3, 4, 5].map((step) => (
              <ProgressStep key={step} $isActive={currentStep === step}>
                <StepIcon
                  $isActive={currentStep === step}
                  $isCompleted={currentStep > step}
                >
                  {currentStep > step ? '✓' : '○'}
                </StepIcon>
                <StepLabel $isActive={currentStep === step} $isCompleted={currentStep > step}>
                  Step {step}
                </StepLabel>
              </ProgressStep>
            ))}
          </ProgressSteps>
        </ProgressContainer>

        {error && (
          <ErrorMessage>
            {error}
          </ErrorMessage>
        )}

        <StepContent>
          {renderStepContent()}
        </StepContent>

        {currentStep < 6 && (
          <NavigationContainer>
            <BackButton
              onClick={handleBack}
              disabled={currentStep === 1}
            >
              ← Back
            </BackButton>

            <NextButton
              onClick={handleNext}
              disabled={!canProceedToNextStep() || loading}
            >
              {loading ? (
                <>
                  ⟳ Processing...
                </>
              ) : (
                <>
                  {currentStep === 5 ?
                    (venposBasket?.data?.finalPrice === 0 ? 'Confirm Free Booking' : 'Complete Booking') :
                    'Continue'
                  } →
                </>
              )}
            </NextButton>
          </NavigationContainer>
        )}
      </BookingContent>

      <Footer />
    </BookingContainer>
  );
};

export default BookingJourney2;
